import './tracklist-app-block.component.scss';

import dayjs from 'dayjs';
import type { FunctionComponent } from 'preact';
import { h } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Provider } from 'react-redux';

import { IconExplicit } from '../../../common/icons/icon-explicit';
import { IconPlayerPause } from '../../../common/icons/icon-player-pause';
import { IconPlayerPlay } from '../../../common/icons/icon-player-play';
import { IconWaveform } from '../../../common/icons/icon-waveform/icon-waveform';
import { useTranslation } from '../../../i18n';
import { createAppStore } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { buttonize } from '../../../utils/a11y.utils';
import { getSearchParams } from '../../../utils/window.utils';
import {
  getAlbumId,
  getArtworkUrl,
  getFeaturedArtists,
  getMainArtists,
  getTrackId,
  isTrackPlayable,
} from '../../models/album.helpers';
import type { Track } from '../../models/album.interface';
import { peekAudioEngineSnapshot } from '../../services/audio-engine';
import { invalidateAlbumCache } from '../../services/music.service';
import { adoptPlayback, playTrack, setMusicIsPlaying } from '../../store/music.reducer';
import {
  selectCurrentMusicAlbumId,
  selectCurrentTrackIndex,
  selectMusicAlbum,
  selectMusicAlbumAccess,
  selectMusicAlbumError,
  selectMusicAlbumLoading,
  selectMusicAlbumNotFound,
  selectMusicHasActivated,
  selectMusicIsPlaying,
} from '../../store/music.selectors';
import {
  DEMO_ALBUM_ID,
  loadAlbum,
  loadAlbumByProductId,
  loadDemoAlbum,
} from '../../store/music.thunk';
import { AudioPlayerComponent } from '../audio-player/audio-player.component';
import { ReleaseCountdownComponent } from '../release-countdown/release-countdown.component';
import type { TracklistAppBlockProps } from './tracklist-app-block.types';

interface ProcessedTrack extends Track {
  mergedArtists: string;
  poster?: string;
}

const TracklistAppBlockContent: FunctionComponent<TracklistAppBlockProps> = ({
  albumId,
  productId,
  mode,
  colorSetting,
  buttonColor,
  buttonTextColor,
  secondaryButtonColor,
  secondaryButtonTextColor,
  tlTextColor,
  tlTrackSeparatorWidth,
  tlTrackSeparatorColor,
  tlShowTrackLength = true,
  tlPaddingTop,
  tlPaddingBottom,
  apTextColor,
  apBackgroundColor,
  apBorderColor,
  apBorderWidth,
  apBorderRadius,
  apProgressBarColor,
  apProgressBackgroundColor,
  apShowTimeElapsed = true,
  apShowArtwork = true,
  apShowPlayer = true,
  showCountdown = false,
  countdownBackgroundColor,
  countdownTextColor,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const contentId = getSearchParams()?.get('content-id') ?? undefined;
  const isDemo = mode === 'demo';
  const effectiveLookupId = isDemo ? DEMO_ALBUM_ID : albumId || contentId || productId || '';
  const currentAlbumId = useAppSelector(selectCurrentMusicAlbumId);
  const currentTrackIndex = useAppSelector(selectCurrentTrackIndex);
  const isPlaying = useAppSelector(selectMusicIsPlaying);
  const album = useAppSelector(selectMusicAlbum(effectiveLookupId));
  const loading = useAppSelector(selectMusicAlbumLoading(effectiveLookupId));
  const error = useAppSelector(selectMusicAlbumError(effectiveLookupId));
  const notFound = useAppSelector(selectMusicAlbumNotFound(effectiveLookupId));
  const access = useAppSelector(selectMusicAlbumAccess(effectiveLookupId));

  useEffect(() => {
    if (isDemo) {
      if (!album) dispatch(loadDemoAlbum());
      return;
    }
    if (!album) {
      if (albumId || contentId) {
        dispatch(loadAlbum((albumId || contentId)!));
      } else if (productId) {
        dispatch(loadAlbumByProductId(productId));
      }
    }
  }, [isDemo, albumId, contentId, productId]);

  const tracks = useMemo((): ProcessedTrack[] => {
    if (!album) return [];
    const poster = getArtworkUrl(album.images);
    const realAlbumId = getAlbumId(album);
    return [...album.tracks]
      .sort((a, b) => a.order - b.order)
      .map((track) => {
        const featuredArtists = getFeaturedArtists(track.parties).map((a) => a.name);
        const title = featuredArtists.length
          ? `${track.title} (feat. ${featuredArtists.join(', ')})`
          : track.title;
        return {
          ...track,
          title,
          mergedArtists: getMainArtists(track.parties)
            .map((a) => a.name)
            .join(', '),
          poster,
          albumId: realAlbumId,
          albumTitle: album.title,
        };
      });
  }, [album]);

  // Demo mode is a styling preview with no audio behind it, so its rows carry no playback id. They
  // are still shown as playable: a merchant is here to see what the block and the player bar look
  // like, and a preview with every row greyed out is not that. Kept local to the block that knows it
  // is a demo rather than taught to isTrackPlayable, which answers only about real audio.
  const canPlay = useCallback((track: Track) => isDemo || isTrackPlayable(track), [isDemo]);
  const anyPlayable = useMemo(() => tracks.some(canPlay), [tracks, canPlay]);

  // Width of the widest duration on the list, in characters. Every row reserves it, so a row with no
  // duration (a masked track) still holds the column and the Exclusive badge beside it lines up with
  // the badges on rows that do. Measured from the data rather than fixed: the backend prints an
  // hour-long track as H:MM:SS, so the widest duration varies by album. Zero when every title is
  // still masked; the empty column and its gap stay, so the badge keeps the same inset it has on a
  // list that does show durations.
  const durationChars = useMemo(
    () => Math.max(0, ...tracks.map((track) => (track.duration ?? '').length)),
    [tracks]
  );

  const hasActivated = useAppSelector(selectMusicHasActivated);

  // The audio engine survives theme section re-renders (variant selection
  // replaces the section DOM, remounting this block with a fresh store). If
  // the engine is still on one of our tracks, adopt it so the bar comes back
  // showing the audio that never stopped (SNGL-7020).
  const adoptAttemptedRef = useRef(false);
  useEffect(() => {
    if (adoptAttemptedRef.current || !tracks.length) return;
    adoptAttemptedRef.current = true;
    const snap = peekAudioEngineSnapshot();
    if (!snap?.playbackId) return;
    if (snap.paused && snap.currentTime === 0) return;
    const index = tracks.findIndex((t) => t.playbackId === snap.playbackId);
    if (index < 0) return;
    dispatch(adoptPlayback({ albumId: effectiveLookupId, index, isPlaying: !snap.paused }));
  }, [tracks]);

  // Token refresh for the player: this block loads albums by product id /
  // demo, so the player's default loadAlbum(albumId) path can't update the
  // album this block reads. Bypass the session cache — this only runs on a
  // playback error, where a cached response is exactly the stale token.
  const refreshTracks = useCallback(() => {
    invalidateAlbumCache();
    if (isDemo) {
      dispatch(loadDemoAlbum());
    } else if (albumId || contentId) {
      dispatch(loadAlbum((albumId || contentId)!));
    } else if (productId) {
      dispatch(loadAlbumByProductId(productId));
    }
  }, [isDemo, albumId, contentId, productId, dispatch]);
  const tlabRef = useRef<HTMLDivElement>(null);
  const [colorSchemeClass, setColorSchemeClass] = useState<string | undefined>(undefined);

  const releaseDate = album?.releaseDate ?? null;
  const presaleDate = album?.presaleDate ?? null;
  const presalePassed = presaleDate ? new Date(presaleDate).getTime() <= Date.now() : false;

  useEffect(() => {
    if (colorSetting !== 'color_scheme_type' || !tlabRef.current) return;
    let el: HTMLElement | null = tlabRef.current.parentElement;
    while (el) {
      const match = Array.from(el.classList).find((c) => c.startsWith('color-scheme-'));
      if (match) {
        setColorSchemeClass(match);
        break;
      }
      el = el.parentElement;
    }
  }, [colorSetting]);

  const handlePlayTrack = (index: number) => {
    if (!tracks[index] || !canPlay(tracks[index])) return;
    if (currentAlbumId === effectiveLookupId && index === currentTrackIndex) {
      dispatch(setMusicIsPlaying(!isPlaying));
    } else {
      dispatch(playTrack({ albumId: effectiveLookupId, index }));
    }
  };

  const cssVars: Record<string, string> = {};
  if (buttonColor) cssVars['--buttonColor'] = buttonColor;
  if (buttonTextColor) cssVars['--buttonTextColor'] = buttonTextColor;
  if (secondaryButtonColor) cssVars['--secondaryButtonColor'] = secondaryButtonColor;
  if (secondaryButtonTextColor) cssVars['--secondaryButtonTextColor'] = secondaryButtonTextColor;
  if (tlTextColor) cssVars['--tlTextColor'] = tlTextColor;
  if (tlTrackSeparatorWidth) cssVars['--tlSeparatorWidth'] = tlTrackSeparatorWidth;
  if (tlTrackSeparatorColor) cssVars['--tlSeparatorColor'] = tlTrackSeparatorColor;
  if (tlPaddingTop) cssVars['--tlPaddingTop'] = tlPaddingTop;
  if (tlPaddingBottom) cssVars['--tlPaddingBottom'] = tlPaddingBottom;
  // Audio player bar theming
  if (apTextColor) cssVars['--apTextColor'] = apTextColor;
  if (apBackgroundColor) cssVars['--apBackgroundColor'] = apBackgroundColor;
  if (apBorderColor) cssVars['--apBorderColor'] = apBorderColor;
  if (apBorderRadius) cssVars['--apBorderRadius'] = apBorderRadius;
  if (apProgressBarColor) cssVars['--apProgressBarColor'] = apProgressBarColor;
  if (apProgressBackgroundColor) cssVars['--apProgressBackgroundColor'] = apProgressBackgroundColor;
  if (countdownBackgroundColor) cssVars['--tlCountdownBgColor'] = countdownBackgroundColor;
  if (countdownTextColor) cssVars['--tlCountdownTextColor'] = countdownTextColor;

  return (
    <div
      ref={tlabRef}
      class={`single-tlab${colorSetting === 'color_scheme_type' ? ' single-color-scheme-type' : ''}`}
      style={cssVars}
    >
      {loading && !album && (
        <div class="single-tlab-loading-placeholder" aria-hidden="true">
          <div class="single-tlab-loading-row" />
          <div class="single-tlab-loading-row" />
          <div class="single-tlab-loading-row" />
        </div>
      )}
      {/* A release still inside its visibility window answers 404 (SNGL-7177). There is nothing
          to show and nothing has gone wrong, so the block renders empty rather than telling a fan
          about an error. */}
      {!loading && error && !notFound && <div class="single-tlab-error">{error}</div>}
      {showCountdown && releaseDate && presalePassed && (
        <ReleaseCountdownComponent
          key={releaseDate}
          releaseDate={releaseDate}
          hideAtZero
          message={t('music.preorderUnlock', { date: dayjs(releaseDate).format('MMMM D, YYYY') })}
        />
      )}
      {!loading && !error && album && (
        <div class="single-tlab-tracks" style={{ '--tlDurationWidth': `${durationChars}ch` }}>
          {tracks.map((track: ProcessedTrack, i: number) => {
            const playable = canPlay(track);
            const pending = track.released === false || track.placeholder === true;
            const isActive =
              playable && currentAlbumId === effectiveLookupId && i === currentTrackIndex;
            const isActiveAndPlaying = isActive && isPlaying;
            return (
              <div
                key={getTrackId(track) ?? i}
                class={`single-tlab-track${isActive ? ' active' : ''}${playable ? '' : ' static'}${pending ? ' pending' : ''}${anyPlayable ? '' : ' no-icon'}`}
                {...(playable ? buttonize(() => handlePlayTrack(i), track.title) : {})}
                aria-current={isActive}
              >
                {playable ? (
                  <span class="single-tlab-track-icon">
                    {isActiveAndPlaying ? (
                      <>
                        <IconWaveform width={20} height={20} animated />
                        <IconPlayerPause width={14} height={14} />
                      </>
                    ) : (
                      <IconPlayerPlay width={14} height={14} />
                    )}
                  </span>
                ) : (
                  anyPlayable && <span />
                )}
                <div class="single-tlab-track-info">
                  <p class="single-tlab-track-title">
                    <span class="single-tlab-track-title-text">{track.title}</span>
                  </p>
                  {(track.explicit || track.mergedArtists) && (
                    <div class="single-tlab-track-artist">
                      {track.explicit && <IconExplicit width={12} height={12} />}
                      {track.mergedArtists && <span>{track.mergedArtists}</span>}
                    </div>
                  )}
                </div>
                <div class="single-tlab-track-end">
                  {track.exclusive && (
                    <span class="single-tlab-track-exclusive">{t('music.exclusive')}</span>
                  )}
                  {tlShowTrackLength && (
                    <span class="single-tlab-track-duration">{track.duration}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {hasActivated && currentAlbumId === effectiveLookupId && (
        <AudioPlayerComponent
          tracks={tracks}
          colorSetting={colorSetting}
          colorSchemeClass={colorSchemeClass}
          barBackgroundColor={apBackgroundColor}
          textColor={apTextColor}
          borderColor={apBorderColor}
          borderWidth={apBorderWidth}
          borderRadius={apBorderRadius}
          progressBarColor={apProgressBarColor}
          progressBackgroundColor={apProgressBackgroundColor}
          buttonColor={buttonColor}
          buttonIconColor={buttonTextColor}
          secondaryButtonColor={secondaryButtonColor}
          secondaryButtonIconColor={secondaryButtonTextColor}
          showTimeElapsed={apShowTimeElapsed}
          showArtwork={apShowArtwork}
          showPlayer={apShowPlayer}
          refreshTracks={refreshTracks}
        />
      )}
    </div>
  );
};

export const TracklistAppBlockComponent: FunctionComponent<TracklistAppBlockProps> = (props) => {
  const [instanceStore] = useState(() => createAppStore());
  return (
    <Provider store={instanceStore}>
      <TracklistAppBlockContent {...props} />
    </Provider>
  );
};
